import { useMemo, useState } from 'react'
import { Button, Chip, Group, iconSizes, List, ListItem, Skeleton, Stack, Text, Tooltip } from '@common/ui'
import { IconArrowsExchange, IconClock, IconHistory, IconTopologyStar3, IconUserCircle } from '@common/ui/icons'
import { com } from '@eidu/web'
import { VersionRecord } from '../../../../../io/api/entity/data/listVersions'
import EntityLink from '../../../../../components/data/EntityLink'
import VersionDiffDialog from './VersionDiffDialog'
import useVersions from './useVersions'
import EntityId = com.eidu.entity.EntityId

type VersionHistoryProps = {
  entityId: EntityId
  initialCompareVersionId?: string
}

type DiffPair = { left: string; right: string }

const absoluteTimestamp = (ms: number): string => new Date(ms).toLocaleString()

const relativeTimestamp = (ms: number, now: number = Date.now()): string => {
  const diffSec = Math.round((ms - now) / 1000)
  const abs = Math.abs(diffSec)
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  if (abs < 60) return rtf.format(diffSec, 'second')
  if (abs < 3600) return rtf.format(Math.round(diffSec / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), 'hour')
  if (abs < 2592000) return rtf.format(Math.round(diffSec / 86400), 'day')
  if (abs < 31536000) return rtf.format(Math.round(diffSec / 2592000), 'month')
  return rtf.format(Math.round(diffSec / 31536000), 'year')
}

const shortVersionId = (versionId: string): string => versionId.split('-')[0] ?? versionId.slice(0, 8)

type VersionRowProps = {
  record: VersionRecord
  isLatest: boolean
  onCompare: () => void
  compareDisabledReason?: string
}

const VersionRow = ({ record, isLatest, onCompare, compareDisabledReason }: VersionRowProps) => (
  <ListItem style={{ paddingBlock: 12, display: 'block' }}>
    <Group justify="space-between" mb="xxs" w="100%">
      <Group gap="xs">
        <Tooltip label={record.versionId}>
          <Text size="sm" ff="monospace" fw={600}>
            {shortVersionId(record.versionId)}
          </Text>
        </Tooltip>
        {isLatest && <Chip tone="platform">Latest</Chip>}
      </Group>
      <Tooltip label={compareDisabledReason ?? 'Compare against latest'}>
        <span>
          <Button
            size="xs"
            variant="outline"
            leftSection={<IconArrowsExchange size={iconSizes.xs} stroke={1.5} />}
            onClick={onCompare}
            disabled={!!compareDisabledReason}
          >
            Compare
          </Button>
        </span>
      </Tooltip>
    </Group>
    <Group gap="md" c="dimmed" style={{ rowGap: 4, flexWrap: 'wrap' }}>
      <Tooltip label={absoluteTimestamp(record.createdAt)}>
        <Group gap="xxs">
          <IconClock size={iconSizes.xs} stroke={1.5} />
          <Text size="xs">{relativeTimestamp(record.createdAt)}</Text>
        </Group>
      </Tooltip>
      <Group gap="xxs" style={{ minWidth: 0 }}>
        <IconUserCircle size={iconSizes.xs} stroke={1.5} />
        <Text size="xs" truncate>
          <EntityLink entityId={record.createdBy} variant="emphasized" showTooltip={false} />
        </Text>
      </Group>
      <Group gap="xxs">
        <IconTopologyStar3 size={iconSizes.xs} stroke={1.5} />
        <Text size="xs">
          {record.dependencyCount} {record.dependencyCount === 1 ? 'dependency' : 'dependencies'}
        </Text>
      </Group>
    </Group>
  </ListItem>
)

const VersionHistory = ({ entityId, initialCompareVersionId }: VersionHistoryProps) => {
  const { records, error } = useVersions(entityId)
  const [overridePair, setOverridePair] = useState<DiffPair | null>(null)
  const [dismissed, setDismissed] = useState(false)

  const autoPair = useMemo<DiffPair | null>(() => {
    if (!initialCompareVersionId || !records) return null
    const idx = records.findIndex((r) => r.versionId === initialCompareVersionId)
    if (idx === -1) return null
    const right = records[idx].versionId
    const left = (records[idx + 1] ?? records[idx]).versionId
    return { left, right }
  }, [initialCompareVersionId, records])

  const diffPair = overridePair ?? (dismissed ? null : autoPair)

  const closeDiff = () => {
    setOverridePair(null)
    setDismissed(true)
  }

  if (error) return <Text c="negative">Failed to load version history: {error.message}</Text>
  if (records === undefined)
    return (
      <List withDividers aria-busy>
        {Array.from({ length: 3 }).map((_, i) => (
          <ListItem key={i} style={{ paddingBlock: 12, display: 'block' }}>
            <Skeleton width={72} height={20} mb="xxs" />
            <Group gap="md">
              <Skeleton width={90} height={16} />
              <Skeleton width={120} height={16} />
              <Skeleton width={100} height={16} />
            </Group>
          </ListItem>
        ))}
      </List>
    )
  if (records.length === 0)
    return (
      <Stack flex={1} align="center" justify="center" ta="center" px="lg" gap="sm" c="dimmed">
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: 'var(--trellis-color-surface-variant)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <IconHistory size={iconSizes.xl} stroke={1.5} color="var(--trellis-color-text-secondary)" />
        </div>
        <Text fw={500} c="var(--trellis-color-text-primary)" mt="xs">
          No versions yet
        </Text>
        <Text size="sm" maw={280}>
          Publish this asset to create an immutable snapshot. Each publish will appear here.
        </Text>
      </Stack>
    )

  const latest = records[0]
  const canCompareBase = records.length >= 2

  return (
    <>
      <List withDividers>
        {records.map((record, index) => {
          const isLatest = index === 0
          const selfCompare = isLatest
          const disabledReason = !canCompareBase
            ? 'Need at least two versions to compare'
            : selfCompare
              ? 'This is the latest version'
              : undefined
          return (
            <VersionRow
              key={record.versionId}
              record={record}
              isLatest={isLatest}
              onCompare={() => setOverridePair({ left: latest.versionId, right: record.versionId })}
              compareDisabledReason={disabledReason}
            />
          )
        })}
      </List>
      {diffPair && (
        <VersionDiffDialog
          onClose={closeDiff}
          records={records}
          initialLeftVersionId={diffPair.left}
          initialRightVersionId={diffPair.right}
        />
      )}
    </>
  )
}

export default VersionHistory
